/** @jsxRuntime classic */
/** @jsx jsx */
import { jsx } from "@emotion/core";
import { usePrevious } from "@react-hookz/web";
import { warnOnce } from "@smartsheet/lodestar-utils";
import { getClientId } from "@smartsheet/lodestar-utils";
import { useForkRef } from "@smartsheet/lodestar-utils";
import { useActive } from "@smartsheet/lodestar-utils";
import { useCombobox, UseComboboxStateChange } from "downshift";
import {
    ButtonHTMLAttributes,
    ChangeEvent,
    HTMLAttributes,
    InputHTMLAttributes,
    KeyboardEvent,
    RefAttributes,
    useCallback,
    useEffect,
    useRef,
    useState,
} from "react";
import { useCombinedThemeMode } from "../../../../util/theme/useCombinedThemeMode";
import { useHovered } from "../../../../util/useHovered";
import { useA11yStatusMessages } from "../../hooks/useA11yStatusMessages";
import { useAutoHighlightWithLoading } from "../../hooks/useAutoHighlightWithLoading";
import { useBlurOnChipBlur } from "../../hooks/useBlurChipOnBlur";
import { useFocused } from "../../hooks/useFocused";
import { useHorizontalResize } from "../../hooks/useHorizontalResize";
import { useMultipleSelectionCustom } from "../../hooks/useMultipleSelectionCustom";
import { useMultiSelectOptionToString } from "../../hooks/useOptionToString";
import { useResetSelection } from "../../hooks/useResetSelection";
import { useSelectFlyout } from "../../hooks/useSelectFlyout";
import { highlightFirstAutomaticallyStateReducer } from "../../reducers/highlightFirstAutomaticallyStateReducer";
import { BaseOptionType, FormatOption } from "../../types";
import {
    useSelectIds,
    isSelectAllMarkerOption,
    isCreatableMarkerOption,
    creatableMarkerOption,
    stopPropagationHandler,
    isSelectV2OptionSeparator,
    findNextValidOptionIndex,
    squashSeparators,
} from "../../utils";
import { Actions } from "../shared/Actions";
import { AutoSizeInput } from "../shared/AutoSizeInput";
import { FuzzySearchResult } from "../shared/FuzzySearchResult";
import { HiddenInput } from "../shared/HiddenInput";
import { Root } from "../shared/Root";
import { SelectClearButton } from "../shared/SelectClearButton";
import { SelectMenu } from "../shared/SelectMenu";
import { SelectPlaceholder } from "../shared/SelectPlaceholder";
import { ToggleButton } from "../shared/ToggleButton";
import { MultiSelectOption } from "./MultiSelectOption";
import { MultiSelectValueContainer } from "./MultiSelectValueContainer";
import { OptionalPropsNowRequired } from "./types";

export interface MultiSelectComboBoxProps<Option extends BaseOptionType>
    extends OptionalPropsNowRequired<Option, HTMLInputElement> {
    /**
     * This set of options includes the select all.
     */
    memoizedOptions: Option[];

    /**
     * The logging hook is taken care of in the parent component.
     */
    onChange: (newOptions: Option[]) => void;
}

export const MultiSelectComboBox = <Option extends BaseOptionType>({
    appearance = "outlined",
    "aria-describedby": ariaDescribedby,
    "aria-label": ariaLabel,
    "aria-labelledby": ariaLabelledby,
    className,
    clientId,
    enableSelectAll,
    formatOption,
    getHiddenInputValue,
    hiddenInputName,
    id,
    innerRef,
    isClearable = true,
    isLoading,
    isOptionDisabled,
    isReadonly,
    menuWidth = "match-control",
    maxMenuHeight,
    maxOptionsUntilVirtualization,
    maxSelectedRows,
    menuId,
    rollupSelectionAfter,
    memoizedOptions,
    menuPlacement = "bottom",
    menuPosition,
    onBlur: providedOnBlur,
    onChange,
    onCreateOption,
    onFocus: providedOnFocus,
    optionHeight,
    options: providedOptions,
    optionToString,
    portalProps,
    selectedOptions,
    validationState,
    filterOption,
    getOptionKey,
    menuIsOpen,
    setMenuIsOpen,
    onMenuIsOpenChanged,
    useFuzzySearch = false,
    inputValue: providedInputValue,
    setInputValue: providedSetInputValue,
    hideDropdownArrow = false,
    formatSelectedValue,
    placeholder,
    onKeyDown: providedOnKeyDown,
    highlightFirstOptionAutomatically = false,
    onPaste: providedOnPaste,
}: MultiSelectComboBoxProps<Option>) => {
    const a11yMessages = useA11yStatusMessages();
    const themeMode = useCombinedThemeMode();

    // null here means "no filter applied" so we can easily know to use the normal options object
    // when there's no filter.
    const [filteredOptions, setFilteredOptions] = useState<Option[] | null>(null);
    const [inputValue, setInputValue] = useState(providedInputValue ?? "");
    const { isFocused, onBlur, onFocus } = useFocused({
        onBlur: providedOnBlur,
        onFocus: providedOnFocus,
    });

    // Tracks the current height of the content box containing the Chips.
    // Update this to a new value to reposition the Flyout.
    const [chipContentHeight, setChipContentHeight] = useState(0);

    // The flyout needs to point to the component container, but otherwise flyout interactions
    // should affect the input.
    const { ref: resizeRef, width: rootWidth } = useHorizontalResize<HTMLDivElement>();
    const localContainerRef = useRef<HTMLDivElement>(null);
    const containerRef = useForkRef(localContainerRef, resizeRef);
    const localInputRef = useRef<HTMLInputElement>(null);
    const inputRef = useForkRef(localInputRef, innerRef ? innerRef : null);

    const isInteractive = !isReadonly;

    const resetFiltering = () => {
        setInputValue("");
        providedSetInputValue?.("");
        setFilteredOptions(null);
    };

    const multiSelectOptionToString = useMultiSelectOptionToString(optionToString, inputValue);

    const {
        isHovered: isToggleHovered,
        onMouseLeave: onToggleMouseLeave,
        onMouseEnter: onToggleMouseEnter,
    } = useHovered<HTMLButtonElement>();

    const {
        isActive: isToggleActive,
        onMouseDown: onTAMouseDown,
        onMouseEnter: onTAMouseEnter,
        onMouseLeave: onTAMouseLeave,
    } = useActive({
        onMouseEnter: onToggleMouseEnter,
        onMouseLeave: onToggleMouseLeave,
    });

    const optionsToDisplay = filteredOptions || memoizedOptions;
    const isCreatable = typeof onCreateOption === "function";

    const doFilter = useCallback(
        (forInputValue: string) => {
            if (!forInputValue) {
                return;
            }
            const inputLowerCase = forInputValue.toLowerCase();
            const filteredItems = memoizedOptions.filter((opt) => {
                // Ignore this one out here so we don't accidentally call optionToString on
                // it.
                if (isSelectAllMarkerOption(opt)) {
                    return false;
                } else if (isSelectV2OptionSeparator(opt)) {
                    return true;
                }
                return filterOption(opt, inputLowerCase);
            });
            const squashedAndFilteredItems = squashSeparators(filteredItems);
            if (isCreatable && forInputValue) {
                setFilteredOptions([creatableMarkerOption as unknown as Option, ...squashedAndFilteredItems]);
            } else {
                setFilteredOptions(squashedAndFilteredItems);
            }
        },
        [filterOption, isCreatable, memoizedOptions]
    );

    const {
        addSelectedItem,
        getDropdownProps,
        getSelectedItemProps,
        isItemSelected,
        removeSelectedItem,
        selectedItems,
        setSelectedItems,
        setActiveIndex,
        activeIndex,
    } = useMultipleSelectionCustom({
        isInteractive,
        itemToString: multiSelectOptionToString,
        rollupSelectionAfter,
        onChange,
        selectedItems: selectedOptions,
    });

    const { resetSelection } = useResetSelection();

    const {
        closeMenu,
        getComboboxProps,
        getInputProps,
        getItemProps,
        getMenuProps,
        getToggleButtonProps,
        highlightedIndex,
        isOpen,
        toggleMenu,
        openMenu,
        setHighlightedIndex,
        selectItem,
    } = useCombobox({
        ...a11yMessages,
        ...useSelectIds("combobox", id),
        ...(menuIsOpen !== undefined ? { isOpen: menuIsOpen } : null),
        circularNavigation: false,
        items: optionsToDisplay,
        inputValue,
        itemToString: multiSelectOptionToString,
        onInputValueChange: ({ inputValue, isOpen }: UseComboboxStateChange<Option>) => {
            // Don't try and filter when the menu is closed. This can be triggered by
            // the selection changing.
            if (isOpen && inputValue) {
                doFilter(inputValue);
            } else {
                resetFiltering();
            }
        },
        onIsOpenChange: ({ isOpen: openState }) => {
            if (process.env.NODE_ENV !== "production") {
                warnOnceForDeprecatedSetMenuIsOpenProp =
                    warnOnceForDeprecatedSetMenuIsOpenProp ??
                    warnOnce(
                        "MultiSelectV2 (combobox): setMenuIsOpen is deprecated. Please use onMenuIsOpenChanged instead."
                    );
                if (setMenuIsOpen) {
                    warnOnceForDeprecatedSetMenuIsOpenProp();
                }
            }
            // setMenuIsOpen is deprecated. Use onMenuIsOpenChanged if it's available instead.
            (onMenuIsOpenChanged ?? setMenuIsOpen)?.(!!openState);
        },
        stateReducer: (state, actionAndChanges) => {
            const { changes, type } = actionAndChanges;

            // Cancel the following events if the input is non-interactive.
            switch (type) {
                case useCombobox.stateChangeTypes.InputKeyDownEscape:
                    // Cancel the escape key handling. We want to handle this key on our own
                    // through the StackOrderManager.
                    return state;

                case useCombobox.stateChangeTypes.InputKeyDownArrowDown:
                case useCombobox.stateChangeTypes.InputKeyDownArrowUp:
                case useCombobox.stateChangeTypes.InputKeyDownHome:
                case useCombobox.stateChangeTypes.InputKeyDownEnd:
                case useCombobox.stateChangeTypes.InputKeyDownEnter:
                case useCombobox.stateChangeTypes.ToggleButtonClick:
                    if (!isInteractive) {
                        return state;
                    }
            }

            switch (type) {
                case useCombobox.stateChangeTypes.InputKeyDownEnter:
                case useCombobox.stateChangeTypes.ItemClick:
                    if (changes.selectedItem && isOptionDisabled?.(changes.selectedItem)) {
                        return state;
                    }
                    return {
                        ...changes,
                        // Retain the menu's state after a selection.
                        highlightedIndex: state.highlightedIndex,
                        isOpen: true,
                    };
            }

            if (changes.highlightedIndex) {
                switch (type) {
                    case useCombobox.stateChangeTypes.InputKeyDownArrowUp:
                    case useCombobox.stateChangeTypes.InputKeyDownArrowDown:
                        if (isSelectV2OptionSeparator(optionsToDisplay[changes.highlightedIndex])) {
                            return {
                                ...changes,
                                highlightedIndex: findNextValidOptionIndex(
                                    optionsToDisplay,
                                    changes.highlightedIndex,
                                    type === useCombobox.stateChangeTypes.InputKeyDownArrowDown
                                ),
                            };
                        }
                }
            }

            if (highlightFirstOptionAutomatically) {
                return highlightFirstAutomaticallyStateReducer<Option>(optionsToDisplay, state, actionAndChanges);
            }

            return changes;
        },
        onStateChange: ({ type, selectedItem }) => {
            switch (type) {
                case useCombobox.stateChangeTypes.InputKeyDownEnter:
                case useCombobox.stateChangeTypes.ItemClick:
                    if (selectedItem) {
                        if (enableSelectAll && isSelectAllMarkerOption(selectedItem)) {
                            if (!allSelected) {
                                setSelectedItems(providedOptions);
                            } else {
                                setSelectedItems([]);
                            }
                            resetSelection(selectItem, 10);
                        } else if (isCreatable && isCreatableMarkerOption(selectedItem) && inputValue) {
                            onCreateOption(inputValue);
                            // Gotta clear out the selected value here as well - the creatable
                            // option is already selected by downshift, so we need to ensure it's
                            // de-selected after the state change completes.
                            resetSelection(selectItem, 10);
                        } else {
                            if (isItemSelected(selectedItem)) {
                                removeSelectedItem(selectedItem);
                                // use a time < 200 ms because the default message that useSelect
                                // reads will debounce anything under 200ms and not call it's
                                // default handler. This means something will be deselected and the
                                // useMultipleSelection a11y status message will be the only one
                                // read out since from useSelect's perspective, the selectedItem
                                // changed from null to null. No change = an empty string for the
                                // a11y message.
                                resetSelection(selectItem, 10);
                            } else {
                                addSelectedItem(selectedItem);
                                // Use a timeout greater than 200ms. This means we'll allow
                                // useSelect to run its default a11y message updater and then
                                // reset the selected item to null.
                                // If we don't do this, there will be no message read on selection.
                                resetSelection(selectItem, 220);
                            }
                        }
                    }
                    // For any selection, clear things out.
                    resetFiltering();
                    break;
                case useCombobox.stateChangeTypes.InputBlur:
                    resetFiltering();
                    break;
                default:
                    break;
            }
        },
    });

    const { flyoutProps, targetRef, ariaOwns, ariaControls } = useSelectFlyout({
        menuId,
        clientId,
        closeMenu,
        isOpen,
        targetRef: containerRef,
        menuPlacement,
        menuPosition,
        portalProps,
        // Update positioning whenever the open state changes, or when the combobox height changes.
        // We need the open state because if it's closed, and something else moves the combobox, the
        // menu needs to recalc or it will still be pointing at the position of its initial render.
        updateKey: `${String(isOpen)}_${chipContentHeight}`,
    });

    // We must ignore the added internal options like "create X" and select all here.
    const allSelected = providedOptions.length === selectedItems.length;

    // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
    const downshiftInputProps = getInputProps({
        value: inputValue,
        onBlur,
        onFocus,
        "aria-describedby": ariaDescribedby,
        "aria-label": ariaLabel,
        "aria-labelledby": ariaLabelledby,
        ...getDropdownProps({
            ref: inputRef,
            onPaste: providedOnPaste,
            onKeyDown: (e: KeyboardEvent<HTMLInputElement>) => {
                providedOnKeyDown?.(e, {
                    highlightedIndex,
                    options: filteredOptions,
                });
                switch (e.key) {
                    case "Tab":
                        if (isOpen) {
                            e.preventDefault();
                            localInputRef.current?.focus();
                            closeMenu();
                            resetFiltering();
                        }
                        break;
                    case "Escape":
                        resetFiltering();
                }
            },
            onFocus: (e: React.FocusEvent<HTMLInputElement>) => {
                onFocus(e);
                setActiveIndex(-2);
            },
        }),
        "aria-controls": ariaControls,
        /**
         * inputValue has to be updated here due updates in different ticks as normal behavior.
         * Setting inputValue here avoids cursor resetting after input value modifications.
         */
        onChange: (e) => {
            const val = (e as ChangeEvent<HTMLInputElement>).target.value;
            setInputValue(val);
            providedSetInputValue?.(val);
        },
    }) as InputHTMLAttributes<HTMLInputElement> & RefAttributes<HTMLInputElement>;

    // There are only a couple things we want to remove from this set of props.
    // We really want the input to still be focusable and interactive, just not do anything.
    if (!isInteractive && downshiftInputProps) {
        downshiftInputProps.onChange = undefined;
        // downshiftInputProps.onKeyDown = undefined;
        downshiftInputProps.readOnly = true;
    }

    const optionRenderer: FormatOption<Option> = (
        option,
        index,
        selectedIndex,
        optionSpreadProps,
        isOptionSelected,
        metadata
    ) => {
        let optionLabel: string | JSX.Element = optionToString(option);

        if (useFuzzySearch) {
            optionLabel = (
                <FuzzySearchResult inputValue={metadata.inputValue} option={option} optionToString={optionToString} />
            );
        }

        return (
            <MultiSelectOption
                height={optionHeight}
                checkedState={isItemSelected(option) ? "checked" : "unchecked"}
                aria-disabled={metadata.isDisabled}
                {...optionSpreadProps}
            >
                {optionLabel}
            </MultiSelectOption>
        );
    };

    const showClearAction = isClearable && selectedItems.length > 0 && !isReadonly;

    const handleChipNavigation = (direction: "tab" | "down" | "up") => {
        switch (direction) {
            case "tab":
                localInputRef.current?.focus();
                break;
            case "down":
                openMenu();
                setActiveIndex(-1);
                setHighlightedIndex(0);
                break;
            case "up":
                openMenu();
                setActiveIndex(-1);
                setHighlightedIndex(memoizedOptions.length - 1);
                break;
        }
    };

    useBlurOnChipBlur({
        ref: localContainerRef,
        activeIndex: activeIndex,
        setActiveIndex: setActiveIndex,
    });

    useEffect(() => {
        if (isOpen && activeIndex > -1) {
            closeMenu();
        }
    }, [isOpen, activeIndex, closeMenu]);

    useEffect(() => {
        if (providedInputValue !== undefined && providedInputValue !== inputValue) {
            setInputValue(providedInputValue);
        }
    }, [doFilter, inputValue, providedInputValue]);

    // If options changes, we'll have to re-run the filtering step and re-render.
    // This is usually for async-loaded options.
    const previousOptions = usePrevious(providedOptions);
    useEffect(() => {
        if (previousOptions !== providedOptions) {
            // Either the user is controlling the input value, or use the uncontrolled input value
            // from downshift.
            if (inputValue) {
                doFilter(inputValue);
            } else {
                setFilteredOptions(null);
            }
        }
    }, [doFilter, providedOptions, previousOptions, inputValue]);

    useAutoHighlightWithLoading<Option>(
        setHighlightedIndex,
        optionsToDisplay,
        isLoading,
        highlightFirstOptionAutomatically,
        isCreatable,
        inputValue
    );

    return (
        <Root
            data-client-id={getClientId(clientId, "-root")}
            className={className}
            isFocused={isFocused || isOpen}
            validationState={validationState || "rest"}
            isReadonly={isReadonly}
            themeMode={themeMode}
            appearance={appearance}
            {...(getComboboxProps({
                ref: targetRef,
                onClick: (e) => {
                    e.stopPropagation();
                    if (isInteractive) {
                        toggleMenu();
                    }
                    // Still focus even if we are disabled.
                    localInputRef.current?.focus();
                },
                "aria-owns": ariaOwns,
            }) as HTMLAttributes<HTMLDivElement>)}
        >
            <HiddenInput
                clientId={clientId}
                getHiddenInputValue={getHiddenInputValue}
                hiddenInputName={hiddenInputName}
                selectionType={selectedItems}
            />
            <MultiSelectValueContainer
                onChipNavigation={handleChipNavigation}
                activeIndex={activeIndex}
                maxSelectedRows={maxSelectedRows}
                numSelected={selectedItems.length}
                onHeightChange={setChipContentHeight}
                getSelectedItemProps={getSelectedItemProps}
                isReadonly={isReadonly}
                rollupSelectionAfter={rollupSelectionAfter}
                optionToString={optionToString}
                removeSelectedItem={removeSelectedItem}
                selectedItems={selectedItems}
                setActiveIndex={setActiveIndex}
                setSelectedItems={setSelectedItems}
                formatSelectedValue={formatSelectedValue}
                id={id}
                clientId={clientId}
            >
                {<AutoSizeInput clientId={clientId} isReadonly={isReadonly} {...downshiftInputProps} />}
                {selectedOptions.length == 0 && placeholder && !downshiftInputProps.value && (
                    <SelectPlaceholder hasContentBefore={false}>{placeholder}</SelectPlaceholder>
                )}
            </MultiSelectValueContainer>
            {isInteractive && (
                <Actions themeMode={themeMode}>
                    <ToggleButton
                        clientId={clientId}
                        isReadonly={isReadonly}
                        hideDropdownArrow={hideDropdownArrow}
                        isHovered={isToggleHovered}
                        isActive={isToggleActive}
                        isOpen={isOpen}
                        onMouseEnter={onTAMouseEnter}
                        onMouseLeave={onTAMouseLeave}
                        onMouseDown={onTAMouseDown}
                        {...(getToggleButtonProps({
                            // Don't let the container handle this.
                            onClick: stopPropagationHandler,
                        }) as ButtonHTMLAttributes<HTMLButtonElement>)}
                    />
                    {showClearAction && (
                        <SelectClearButton
                            closeMenu={closeMenu}
                            setActiveIndex={setActiveIndex}
                            clearSelectedItems={() => setSelectedItems([])}
                            refocusTargetElement={() => {
                                localInputRef.current?.focus();
                            }}
                            isMultiSelect
                            resetFiltering={resetFiltering}
                        />
                    )}
                </Actions>
            )}
            <SelectMenu<Option>
                formatOption={formatOption || optionRenderer}
                isLoading={isLoading}
                menuWidth={menuWidth}
                maxMenuHeight={maxMenuHeight}
                maxOptionsUntilVirtualization={maxOptionsUntilVirtualization}
                optionHeight={optionHeight}
                getItemProps={getItemProps}
                getMenuProps={getMenuProps}
                highlightedIndex={highlightedIndex}
                isOpen={isOpen}
                isOptionDisabled={isOptionDisabled}
                flyoutProps={flyoutProps}
                optionToString={optionToString}
                options={optionsToDisplay}
                targetWidth={rootWidth}
                allSelected={allSelected}
                enableSelectAll={enableSelectAll}
                inputValue={inputValue}
                selectedItems={selectedItems}
                getOptionKey={getOptionKey}
            />
        </Root>
    );
};

let warnOnceForDeprecatedSetMenuIsOpenProp: undefined | (() => void);
